<template>
  <v-dialog
    v-model="processDialog"
    width="540"
    overlay-color="rgba(39, 50, 64)"
    overlay-opacity="0.6"
    :fullscreen="$vuetify.breakpoint.xs ? true : false"
    persistent
    :transition="false"
  >
    <v-card>
      <v-card-title
        class="header"
        primary-title
      >
        <b>{{ $t('withdrawals.withdrawals') }}</b>
      </v-card-title>
      
      <v-card-text
        v-if="withdrawal.status == 'new'"
        class="mt-5"
      >
        {{ $t('withdrawals.changeToPending') }}<br>
        <v-btn
          class="mt-5 mb-5"
          text
          small
          outlined
          :disabled="loading"
          @click="process"
        >
          <span>{{ $t('withdrawals.changeButton') }}</span>
        </v-btn>
      </v-card-text>
      <v-card-text v-else-if="withdrawal.wallet_address">
        <v-flex mt-5>
          <small>{{ $t('withdrawals.pleaseTransfer') }}</small>
        </v-flex>
        <v-flex mt-5>
          <small>{{ $t('withdrawals.wallet') }}</small><br>
          <b>{{ withdrawal.wallet_address }}</b>
          <v-icon
            v-clipboard:copy="withdrawal.wallet_address"
            style="cursor:pointer; font-size:16px; margin:5px;"
            @click="copy('wallet_copied')"
          >
            mdi-content-copy
          </v-icon>
          <small v-if="wallet_copied">{{ $t('globals.copied') }}</small>
        </v-flex>
        <v-flex mt-5>
          <small>{{ $t('withdrawals.amount') }}</small><br>
          <b>{{ (withdrawal.amount - withdrawal.fee) }} {{ withdrawal.currency }}</b>
          <v-icon
            v-clipboard:copy="(withdrawal.amount - withdrawal.fee)"
            style="cursor:pointer; font-size:16px; margin:5px;"
            @click="copy('amount_copied')"
          >
            mdi-content-copy
          </v-icon>
          <small v-if="amount_copied">{{ $t('globals.copied') }}</small>
        </v-flex>
        <v-flex mt-5>
          <small>{{ $t('withdrawals.blockchain') }}</small><br>
          <b>{{ withdrawal.blockchain }}</b>
        </v-flex>
        <v-flex mt-10>
          <small>{{ $t('withdrawals.afterTransfered') }}</small>
          <v-text-field
            v-model.trim="form.txn_id"
            class="mb-5"
            name="txn-id"
            :label="$t('withdrawals.txnId') +'*'"
            :error-messages="TxnIdErrors"
            @input="$v.form.txn_id.$touch()"
            @blur="$v.form.txn_id.$touch()"
            @paste="$v.form.txn_id.$touch()"
          />
        </v-flex>
      </v-card-text>
      <v-card-text v-else>
        <v-flex mt-5>
          <small>{{ $t('withdrawals.pleaseTransferFiat') }}</small>
        </v-flex>
        <v-flex
          xs12
          md12
          mt-5
        >
          <v-layout
            v-if="withdrawal.type === 'card'"
            align-start
            justify-start
            column
            wrap
          >
            <v-flex>
              <div class="details-wrapper">
                <small>{{ $t('withdrawals.cardNumber') }}: </small>
                <b style="overflow-wrap: break-word;">
                  {{ withdrawal.card_number }}
                </b>
              </div>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-clipboard:copy="withdrawal.card_number"
                    style="cursor:pointer; font-size:16px; margin:5px;"
                    v-bind="attrs"
                    @click="
                      copyToClipboard(
                        on,
                        $event,
                        withdrawal.card_number
                      )
                    "
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                {{ $t("globals.copied") }}
              </v-tooltip>
            </v-flex>
          </v-layout>
          <v-layout
            v-if="withdrawal.type === 'swift'"
            align-start
            justify-start
            column
            wrap
          >
            <v-flex
              v-if="withdrawal.iban"
              class="d-flex"
              xs12
            >
              <div class="details-wrapper">
                <small>IBAN: </small>
                <b style="overflow-wrap: break-word;">
                  {{ withdrawal.iban }}
                </b>
              </div>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-clipboard:copy="withdrawal.iban"
                    style="cursor:pointer; font-size:16px; margin:5px;"
                    v-bind="attrs"
                    @click="
                      copyToClipboard(
                        on,
                        $event,
                        withdrawal.iban
                      )
                    "
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                {{ $t("globals.copied") }}
              </v-tooltip>
            </v-flex>
            <v-flex
              v-if="withdrawal.name"
              class="d-flex"
              xs12
            >
              <div class="details-wrapper">
                <small>{{ $t('withdrawals.fullNameRecipient') }}: </small>
                <b style="overflow-wrap: break-word;">
                  {{ withdrawal.name }}
                </b>
              </div>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-clipboard:copy="withdrawal.name"
                    style="cursor:pointer; font-size:16px; margin:5px;"
                    v-bind="attrs"
                    @click="
                      copyToClipboard(
                        on,
                        $event,
                        withdrawal.name
                      )
                    "
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                {{ $t("globals.copied") }}
              </v-tooltip>
            </v-flex>
            <v-flex
              v-if="withdrawal.address"
              class="d-flex"
              xs12
            >
              <div class="details-wrapper">
                <small>{{ $t('withdrawals.addressRecipient') }}: </small>
                <b style="overflow-wrap: break-word;">
                  {{ withdrawal.address }}
                </b>
              </div>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-clipboard:copy="withdrawal.address"
                    style="cursor:pointer; font-size:16px; margin:5px;"
                    v-bind="attrs"
                    @click="
                      copyToClipboard(
                        on,
                        $event,
                        withdrawal.address
                      )
                    "
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                {{ $t("globals.copied") }}
              </v-tooltip>
            </v-flex>
            <v-flex
              v-if="withdrawal.bank_name"
              class="d-flex"
              xs12
            >
              <div class="details-wrapper">
                <small>{{ $t('withdrawals.nameBankRecipient') }}: </small>
                <b style="overflow-wrap: break-word;">
                  {{ withdrawal.bank_name }}
                </b>
              </div>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-clipboard:copy="withdrawal.bank_name"
                    style="cursor:pointer; font-size:16px; margin:5px;"
                    v-bind="attrs"
                    @click="
                      copyToClipboard(
                        on,
                        $event,
                        withdrawal.bank_name
                      )
                    "
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                {{ $t("globals.copied") }}
              </v-tooltip>
            </v-flex>
            <v-flex
              v-if="withdrawal.bank_address"
              class="d-flex"
              xs12
            >
              <div class="details-wrapper">
                <small>{{ $t('withdrawals.addressBankRecipient') }}: </small>
                <b style="overflow-wrap: break-word;">
                  {{ withdrawal.bank_address }}
                </b>
              </div>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-clipboard:copy="withdrawal.bank_address"
                    style="cursor:pointer; font-size:16px; margin:5px;"
                    v-bind="attrs"
                    @click="
                      copyToClipboard(
                        on,
                        $event,
                        withdrawal.bank_address
                      )
                    "
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                {{ $t("globals.copied") }}
              </v-tooltip>
            </v-flex>
            <v-flex
              v-if="withdrawal.bic"
              class="d-flex"
              xs12
            >
              <div class="details-wrapper">
                <small>{{ $t('withdrawals.BICcodeBankRecipient') }}: </small>
                <b style="overflow-wrap: break-word;">
                  {{ withdrawal.bic }}
                </b>
              </div>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-clipboard:copy="withdrawal.bic"
                    style="cursor:pointer; font-size:16px; margin:5px;"
                    v-bind="attrs"
                    @click="
                      copyToClipboard(
                        on,
                        $event,
                        withdrawal.bic
                      )
                    "
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                {{ $t("globals.copied") }}
              </v-tooltip>
            </v-flex>
          </v-layout>
          <v-layout
            v-if="withdrawal.type === 'bank'"
            align-start
            justify-start
            column
            wrap
          >
            <v-flex
              v-if="withdrawal.iban"
              class="d-flex"
              xs12
            >
              <div class="details-wrapper">
                <small>IBAN: </small>
                <b style="overflow-wrap: break-word;">
                  {{ withdrawal.iban }}
                </b>
              </div>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-clipboard:copy="withdrawal.iban"
                    style="cursor:pointer; font-size:16px; margin:5px;"
                    v-bind="attrs"
                    @click="
                      copyToClipboard(
                        on,
                        $event,
                        withdrawal.iban
                      )
                    "
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                {{ $t("globals.copied") }}
              </v-tooltip>
            </v-flex>
            <v-flex
              v-if="withdrawal.name"
              class="d-flex"
              xs12
            >
              <div class="details-wrapper">
                <small>{{ $t('withdrawals.fullNameRecipient') }}: </small>
                <b style="overflow-wrap: break-word;">
                  {{ withdrawal.name }}
                </b>
              </div>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-clipboard:copy="withdrawal.name"
                    style="cursor:pointer; font-size:16px; margin:5px;"
                    v-bind="attrs"
                    @click="
                      copyToClipboard(
                        on,
                        $event,
                        withdrawal.name
                      )
                    "
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                {{ $t("globals.copied") }}
              </v-tooltip>
            </v-flex>
            <v-flex
              v-if="withdrawal.ipn"
              class="d-flex"
              xs12
            >
              <div class="details-wrapper">
                <small>{{ $t('withdrawals.recipientEIN') }}: </small>
                <b style="overflow-wrap: break-word;">
                  {{ withdrawal.ipn }}
                </b>
              </div>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-clipboard:copy="withdrawal.ipn"
                    style="cursor:pointer; font-size:16px; margin:5px;"
                    v-bind="attrs"
                    @click="
                      copyToClipboard(
                        on,
                        $event,
                        withdrawal.ipn
                      )
                    "
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                {{ $t("globals.copied") }}
              </v-tooltip>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex mt-5>
          <small>{{ $t('withdrawals.amount') }}</small><br>
          <b>{{ (withdrawal.amount - withdrawal.fee) }} {{ withdrawal.currency }}</b>
          <v-icon
            v-clipboard:copy="(withdrawal.amount - withdrawal.fee)"
            style="cursor:pointer; font-size:16px; margin:5px;"
            @click="copy('amount_copied')"
          >
            mdi-content-copy
          </v-icon>
          <small v-if="amount_copied">{{ $t('globals.copied') }}</small>
        </v-flex>
        <v-flex mt-10>
          <small>{{ $t('withdrawals.afterTransfered') }}</small>
          <v-text-field
            v-model.trim="form.txn_id"
            class="mb-5"
            name="txn-id"
            :label="$t('withdrawals.txnId') +'*'"
            :error-messages="TxnIdErrors"
            @input="$v.form.txn_id.$touch()"
            @blur="$v.form.txn_id.$touch()"
            @paste="$v.form.txn_id.$touch()"
          />
        </v-flex>
      </v-card-text>
      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          :disabled="loading"
          @click="processDialog = false"
        >
          <span>{{ $t('globals.cancelButton') }}</span>
        </v-btn>
        <v-btn
          color="primary"
          :disabled="loading || ($v.$invalid)"
          :loading="loading"
          @click="complete()"
        >
          {{ $t('withdrawals.completeButton') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>


import PROCESS_WITHDRAWAL_MUTATION from "@/graphql/mutations/ProcessWithdrawal.gql";
import COMPLETE_WITHDRAWAL_MUTATION from "@/graphql/mutations/CompleteWithdrawal.gql";


import { validationMixin } from "vuelidate"
import {
  required,
} from "vuelidate/lib/validators"

export default {
  name: "ProcessDialog",
  mixins: [validationMixin],
  props: {
    withdrawal: {
      type: Object,
      default() {
        return null;
      }
    },
    showDialog: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      wallet_copied: false,
      amount_copied: false,
      loading: false,
      form: {
        id: this.withdrawal.id,
        txn_id: null,
      }
    };
  },
  validations() {
    return {
      form: this.rules
    };
  },
  computed: {
    processDialog: {
      get() {
        return this.showDialog;
      },
      set(val) {
        this.$emit('closeProcessDialog', false);
      }
    },
    rules() {
      var form = {
        txn_id: {
          required,
        },
      };
      return form;
    },
    TxnIdErrors() {
      const errors = [];
      if (!this.$v.form.txn_id.$dirty) return errors;
      !this.$v.form.txn_id.required && errors.push(this.$t("projects.reqField"));
      return errors;
    },
  },
  methods: {
    copy(value) {
      this[value] = true
      setTimeout(()=>{
         this[value] = false
      }, 800)
    },
    copyToClipboard({ mouseenter, mouseleave }, e, input) {
      navigator.clipboard.writeText(input);
      mouseenter(e);
      setTimeout(() => mouseleave(e), 800);
    },
    process() {
      const form = {id: this.withdrawal.id}
      this.$apollo
        .mutate({
          mutation: PROCESS_WITHDRAWAL_MUTATION,
          variables: form
        })
        .then(data => {
          this.loading = false;
          this.withdrawal.status = 'pending'
          this.$parent.$apollo.queries.whiteLabelWithdrawals.refresh();
        })
        .catch(error => {
          this.loading = false;
          if (error.graphQLErrors[0].extensions.category == 'authorization' && error.graphQLErrors[0].message === "This action is unauthorized.") {
            this.$store.commit("app/setShowPermission");
            return;
          }
          this.errorHandler(error);
        });
    },
    complete() {
      this.$apollo
        .mutate({
          mutation: COMPLETE_WITHDRAWAL_MUTATION,
          variables: this.form
        })
        .then(data => {
          this.loading = false;
          this.$store.commit('app/setNotifier', this.$t('globals.success'));
          this.processDialog = false
          this.$parent.$apollo.queries.whiteLabelWithdrawals.refetch();
        })
        .catch(error => {
          this.loading = false;
          if (error.graphQLErrors[0].extensions.category == 'authorization' && error.graphQLErrors[0].message === "This action is unauthorized.") {
            this.$store.commit("app/setShowPermission");
            return;
          }
          this.errorHandler(error);
        });
    }
  },
};
</script>

<style lang="scss" >
.details-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: 12px;
}
</style>
